import React from "react";
import AppCounter from "../../../../components/AppCounter";
import AppField from "../../../../components/AppField";
import AppFieldGrid from "../../../../components/AppFieldGrid";
import AppFieldStack from "../../../../components/AppFieldStack";
import AppInput from "../../../../components/AppInput";
import AppSelect from "../../../../components/AppSelect";
import AppTextarea from "../../../../components/AppTextarea";
import { IconRoom, IconSettings } from "../../../../icons";
import DocsNiceToKnow from "../../shared/DocsNiceToKnow";
import DocsPreviewPanel from "../../shared/DocsPreviewPanel";
import DocsSectionCard from "../../shared/DocsSectionCard";

const roomOptions = [
  { value: "aula", label: "Aula" },
  { value: "b102", label: "B102" },
  { value: "c203", label: "C203" },
];

const groupedOptions = [
  {
    label: "Pflichtfächer",
    options: [
      { value: "math", label: "Mathematik" },
      { value: "german", label: "Deutsch" },
      { value: "english", label: "Englisch" },
    ],
  },
  {
    label: "Wahlfächer",
    options: [
      { value: "music", label: "Musik" },
      { value: "design", label: "Design" },
    ],
  },
];

const anatomyNotes = [
  "Die gezeigte Anatomy gilt für alle Controls",
  "Das eigentliche Control bleibt immer ein Child: AppInput, AppSelect, AppTextarea oder AppCounter.",
];

const stateNotes = [
  "Error Text je nach Kontext formulieren",
];

const sizeNotes = [
  "Es gibt zwei Grössen von Fields: SM & MD",
  "MD = Default in Software! Für alle Fields in einem Arbeits-Dialog, Formular, etc.",
  "SM = Spezialfall. Nur für Fields verwenden, die selten bearbeitet werden und eher als Anzeige dienen.."
];

const layoutInternalNotes = [
  "Layout innerhalb eines einzelnen AppFields.",
  "stacked ist der Standard: Label oben, Control darunter.",
  "inline setzt Label links und Control rechts.",
  "labelWidth begrenzt im inline Layout die linke Label-Seite.",
];

const layoutExternalNotes = [
  "AppFieldStack = mehrere Fields untereinander.",
  "AppFieldGrid = mehrere Fields nebeneinander.",
  "Stack: vertikaler Abstand SM 8px, MD 12px, LG 16px.",
  "Grid: vertikaler Abstand SM 8px, MD 12px, LG 16px; horizontal immer 12px.",
  "span macht einzelne Fields im Grid breiter.",
];

const layoutExternalRules = [
  "Layout SM = Stacked Fields",
  "Layout LG = Inline Fields",
  "Layout MD nur für Ausnahmen (Bsp. Brand-Select).",
];

const inputNotes = [
  "Placeholder setzen!",
  "Search + clearable ist zb. in unserer Toolbar",
  "Search ist zb. in unseren searchable selects",
  "trailingText eignet sich für Suffix. (Z.B. % / Lektionen /...)",
];

const selectNotes = [
  "Placeholder: 'Bitte wählen' → Ausnahme OptionalSelect, dort macht das keinen Sinn.",
  "Die ganzen Varianten können frei miteinander kombiniert werden.",
  "Ein 'Alle Auswählen' kann man sich künstlich erstellen: Grouped Multi mit einer Gruppe 'Alle'.",
];

const textareaNotes = [
  "Placeholder setzen!",
  "rows definiert die sichtbare Höhe.",
  "resize steuert manuelles Vergrössern; resize=\"none\" für fixe Höhe.",
];

const counterNotes = [
  "Segmentiertes Plus/Minus-Control für kleine Zahlenbereiche.",
  "min/max begrenzen den Wert und deaktivieren Buttons an der Grenze.",
  "step bestimmt die Schrittweite.",
  "formatValue formatiert den sichtbaren Wert.",
  "AppCounter hat keinen Placeholder.",
];

function FieldsSection({
  title,
  notes,
  children,
  wide = false,
  compactPreview = false,
  foundation = false,
  highlightTitle = null,
  highlightItems = [],
}) {
  const classes = [
    "ds-docs-fields-section-grid",
    wide ? "ds-docs-fields-section-grid--wide" : "",
    compactPreview ? "ds-docs-fields-section-grid--compact-preview" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={classes}>
      <DocsPreviewPanel
        title={title}
        label={foundation ? "GRUNDLAGEN" : "VORSCHAU"}
        icon={foundation ? IconSettings : undefined}
        className={foundation ? "ds-docs-preview-panel--foundation" : ""}
      >
        <div className="ds-docs-preview-surface ds-docs-fields-preview">{children}</div>
      </DocsPreviewPanel>
      <DocsNiceToKnow items={notes} highlightTitle={highlightTitle} highlightItems={highlightItems} />
    </div>
  );
}

function ExampleTile({ title, children }) {
  return (
    <div className="ds-docs-fields-example-tile">
      <div className="ds-docs-fields-example-title">{title}</div>
      {children}
    </div>
  );
}

function PreviewRow({ label, children }) {
  return (
    <div className="ds-docs-fields-preview-row">
      <div className="ds-docs-fields-preview-row__label">{label}</div>
      <div className="ds-docs-fields-preview-row__content">{children}</div>
    </div>
  );
}

function AppFieldAnatomyPreview() {
  return (
    <div className="ds-docs-fields-example-grid">
      <ExampleTile title="Default">
        <AppField label="Raum">
          <AppInput placeholder="Placeholder" />
        </AppField>
      </ExampleTile>

      <ExampleTile title="Required">
        <AppField label="Fach" required>
          <AppInput defaultValue="Mathematik" />
        </AppField>
      </ExampleTile>

      <ExampleTile title="Help">
        <AppField label="Kürzel" help="Kurzer Hinweis unter dem Field.">
          <AppInput placeholder="z.B. B102" />
        </AppField>
      </ExampleTile>
    </div>
  );
}

function AppFieldStatesPreview() {
  return (
    <div className="ds-docs-fields-example-grid">
      <ExampleTile title="Error">
        <AppField label="Raum" error="Dieses Feld ist erforderlich.">
          <AppInput invalid placeholder="Raum eingeben" />
        </AppField>
      </ExampleTile>

      <ExampleTile title="Disabled">
        <AppField label="Status">
          <AppInput disabled defaultValue="Gesperrt" />
        </AppField>
      </ExampleTile>

      <ExampleTile title="ReadOnly">
        <AppField label="Status">
          <AppInput readOnly defaultValue="LookiLooki" />
        </AppField>
      </ExampleTile>
    </div>
  );
}

function AppFieldSizesPreview() {
  return (
    <div className="ds-docs-fields-size-grid">
      <ExampleTile title="Size SM (30 px)">
        <AppField size="sm" label="Raum">
          <AppInput placeholder="Raum eingeben" />
        </AppField>
      </ExampleTile>

      <ExampleTile title="Size MD (34 px)">
        <AppField size="md" label="Raum">
          <AppInput placeholder="Raum eingeben" />
        </AppField>
      </ExampleTile>
    </div>
  );
}

function AppFieldLayoutInternalPreview() {
  return (
    <div className="ds-docs-fields-layout-preview">
      <div className="ds-docs-fields-layout-grid">
        <ExampleTile title="Stacked">
          <AppField label="Raum">
            <AppInput defaultValue="B102" />
          </AppField>
        </ExampleTile>

        <ExampleTile title="Inline">
          <AppField label="Raum" layout="inline" labelWidth="96px" bodyWidth="180px">
            <AppInput defaultValue="B102" />
          </AppField>
        </ExampleTile>
      </div>
    </div>
  );
}

function AppFieldLayoutExternalPreview() {
  return (
    <div className="ds-docs-fields-layout-preview">
      <div className="ds-docs-fields-layout-grid">
        <ExampleTile title="AppFieldStack (MD)">
          <AppFieldStack>
            <AppField label="Fach">
              <AppInput defaultValue="Mathematik" />
            </AppField>
            <AppField label="Raum">
              <AppSelect options={roomOptions} defaultValue="b102" />
            </AppField>
          </AppFieldStack>
        </ExampleTile>

        <ExampleTile title="AppFieldGrid (MD)">
          <AppFieldGrid columns={2}>
            <AppField label="Fach">
              <AppInput defaultValue="Deutsch" />
            </AppField>
            <AppField label="Raum">
              <AppSelect options={roomOptions} defaultValue="aula" />
            </AppField>
            <AppField label="Notiz" span={2}>
              <AppTextarea rows={2} defaultValue="Span über zwei Spalten." resize="none" />
            </AppField>
          </AppFieldGrid>
        </ExampleTile>
      </div>
    </div>
  );
}

function InputVariants() {
  return (
    <div className="ds-docs-fields-preview-stack">
      <PreviewRow label="Text">
        <AppField label="Raum">
          <AppInput defaultValue="B102" />
        </AppField>
      </PreviewRow>
      <PreviewRow label="Search">
        <AppField label="Suche">
          <AppInput type="search" placeholder="Fach suchen" />
        </AppField>
      </PreviewRow>
      <PreviewRow label="Clearable">
        <AppField label="Suche">
          <AppInput type="search" clearable defaultValue="Mathematik" />
        </AppField>
      </PreviewRow>
      <PreviewRow label="Suffix">
        <AppField label="Pensum">
          <AppInput defaultValue="80" trailingText="%" />
        </AppField>
      </PreviewRow>
    </div>
  );
}

function SelectVariants() {
  return (
    <div className="ds-docs-fields-preview-stack">
      <PreviewRow label="Single">
        <AppField label="Raum">
          <AppSelect options={roomOptions} defaultValue="b102" />
        </AppField>
      </PreviewRow>
      <PreviewRow label="Optional Single">
        <AppField label="Bevorzugter Raum">
          <AppSelect
            options={roomOptions}
            allowEmpty
            placeholder="-"
            emptyOptionLabel="Keiner"
          />
        </AppField>
      </PreviewRow>
      <PreviewRow label="Multi">
        <AppField label="Räume">
          <AppSelect options={roomOptions} multiple defaultValue={["aula", "b102"]} />
        </AppField>
      </PreviewRow>
      <PreviewRow label="Searchable">
        <AppField label="Raum">
          <AppSelect options={roomOptions} searchable placeholder="Raum suchen" />
        </AppField>
      </PreviewRow>
      <PreviewRow label="Groups">
        <AppField label="Fach">
          <AppSelect options={groupedOptions} defaultValue="math" />
        </AppField>
      </PreviewRow>
      <PreviewRow label="Grouped Multi">
        <AppField label="Fächer">
          <AppSelect options={groupedOptions} multiple defaultValue={["math", "design"]} />
        </AppField>
      </PreviewRow>
      <PreviewRow label="Brand">
        <AppField
          layout="inline"
          labelWidth="76px"
          bodyMaxWidth="120px"
          label={
            <span className="ds-field__label-content">
              <span className="ds-field__label-leading-icon" aria-hidden="true">
                <IconRoom />
              </span>
              <span>Raum</span>
            </span>
          }
        >
          <AppSelect variant="brand" options={roomOptions} defaultValue="aula" />
        </AppField>
      </PreviewRow>
    </div>
  );
}

function TextareaVariants() {
  return (
    <div className="ds-docs-fields-preview-stack">
      <PreviewRow label="Default">
        <AppField label="Notiz">
          <AppTextarea rows={3} placeholder="Notiz erfassen" />
        </AppField>
      </PreviewRow>
      <PreviewRow label="Resize none">
        <AppField label="Kommentar">
          <AppTextarea rows={3} resize="none" defaultValue="Fixe Höhe ohne manuelles Resize." />
        </AppField>
      </PreviewRow>
    </div>
  );
}

function CounterVariants() {
  return (
    <div className="ds-docs-fields-preview-stack">
      <PreviewRow label="Default">
        <AppField label="Lektionen">
          <AppCounter defaultValue={4} min={1} max={12} />
        </AppField>
      </PreviewRow>
      <PreviewRow label="Step">
        <AppField label="Dauer">
          <AppCounter defaultValue={30} min={0} max={120} step={15} />
        </AppField>
      </PreviewRow>
      <PreviewRow label="Format">
        <AppField label="Pensum">
          <AppCounter defaultValue={80} min={0} max={100} step={10} formatValue={(value) => `${value}%`} />
        </AppField>
      </PreviewRow>
    </div>
  );
}

export default function FieldsDocs() {
  return (
    <DocsSectionCard title="Fields">
      <div className="ds-docs-fields-new">
        <FieldsSection title="AppField Anatomy" notes={anatomyNotes} foundation>
          <AppFieldAnatomyPreview />
        </FieldsSection>

        <FieldsSection title="AppField States" notes={stateNotes} foundation>
          <AppFieldStatesPreview />
        </FieldsSection>

        <FieldsSection title="AppField Sizes" notes={sizeNotes} foundation>
          <AppFieldSizesPreview />
        </FieldsSection>

        <FieldsSection title="AppField Layout INTERN" notes={layoutInternalNotes} foundation>
          <AppFieldLayoutInternalPreview />
        </FieldsSection>

        <FieldsSection
          title="AppField Layout EXTERN"
          notes={layoutExternalNotes}
          highlightTitle="Anwendungsregeln"
          highlightItems={layoutExternalRules}
          wide
          foundation
        >
          <AppFieldLayoutExternalPreview />
        </FieldsSection>

        <FieldsSection title="AppInput" notes={inputNotes} compactPreview>
          <InputVariants />
        </FieldsSection>

        <FieldsSection title="AppSelect" notes={selectNotes} compactPreview>
          <SelectVariants />
        </FieldsSection>

        <FieldsSection title="AppTextarea" notes={textareaNotes} compactPreview>
          <TextareaVariants />
        </FieldsSection>

        <FieldsSection title="AppCounter" notes={counterNotes} compactPreview>
          <CounterVariants />
        </FieldsSection>
      </div>
    </DocsSectionCard>
  );
}
